import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getProductMaxCartQuantity } from "../../context/CartContext";
import { getLineItemUnitPrice, cartLineKey } from "../../utils/productVariants";
import { useVendorStore } from "../../context/VendorStoreContext";

const CartDrawer = ({
  showCart,
  setShowCart,
  cart,
  updateQuantity,
  variant = "right",
}) => {
  const navigate = useNavigate();
  const { storeSlug } = useVendorStore();
  const checkoutPath = storeSlug ? `/${storeSlug}/checkout` : "/checkout";
  const isBottom = variant === "bottom";

  useEffect(() => {
    if (showCart) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showCart]);

  const total = cart.reduce(
    (acc, { product, quantity, variantOptionId }) =>
      acc + getLineItemUnitPrice(product, variantOptionId) * quantity,
    0,
  );

  const itemCount = cart.reduce((n, { quantity }) => n + quantity, 0);

  const rightPanelStyle = useMemo(
    () => ({
      top: "0.75rem",
      bottom: "0.75rem",
      right: "0.75rem",
      width: "min(24rem, calc(100vw - 1.5rem))",
      maxHeight: "min(calc(100dvh - 1.5rem), calc(100vh - 1.5rem))",
    }),
    [],
  );

  const bottomPanelStyle = useMemo(
    () => ({
      left: "0.5rem",
      right: "0.5rem",
      bottom: "max(0.5rem, env(safe-area-inset-bottom, 0px))",
      maxHeight: "min(88vh, calc(100dvh - env(safe-area-inset-bottom, 0px) - 1rem))",
    }),
    [],
  );

  if (!showCart) return null;

  const content = (
    <>
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm"
        onClick={() => setShowCart(false)}
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        style={isBottom ? bottomPanelStyle : rightPanelStyle}
        className={`fixed z-[210] flex flex-col overflow-hidden bg-white ring-1 ring-black/5 ${
          isBottom
            ? "animate-slideUp rounded-3xl border border-stone-200/90 shadow-[0_12px_48px_rgba(0,0,0,0.2)]"
            : "rounded-2xl border border-orange-100 shadow-2xl"
        }`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-stone-100 px-4 py-3 sm:px-5 sm:py-4">
          <div>
            <h3 id="cart-drawer-title" className="text-lg font-bold text-orange-600">
              Your cart
            </h3>
            {isBottom && cart.length > 0 ? (
              <p className="text-xs font-medium text-stone-500">
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setShowCart(false)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-xl font-bold text-stone-500 transition hover:bg-stone-100 hover:text-red-600"
            aria-label="Close cart"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-3 sm:px-5 sm:py-4">
          {cart.length === 0 ? (
            <p className="mt-6 text-center text-stone-500">Your cart is empty.</p>
          ) : (
            <ul className="space-y-4">
              {cart.map(({ product, quantity, variantOptionId }) => {
                const maxQ = getProductMaxCartQuantity(
                  product,
                  variantOptionId,
                );
                const capped = Number.isFinite(maxQ) && quantity >= maxQ;
                const unit = getLineItemUnitPrice(product, variantOptionId);
                return (
                <li
                  key={cartLineKey(product._id, variantOptionId)}
                  className="flex gap-3 border-b border-stone-100 pb-4 last:border-0 sm:gap-4"
                >
                  <img
                    src={product.images?.[0]}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-lg border border-stone-100 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-semibold leading-snug text-stone-900">
                      {product.name}
                    </p>
                    {variantOptionId && product.variantGroup?.name ? (
                      <p className="mt-0.5 text-xs font-medium text-stone-500">
                        {product.variantGroup.name}
                      </p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-lg leading-none transition hover:bg-stone-100 active:scale-95"
                        onClick={() =>
                          updateQuantity(product._id, -1, variantOptionId || "")
                        }
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="min-w-[1.25rem] text-center text-sm font-bold tabular-nums">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        disabled={capped}
                        title={capped ? `Only ${maxQ} in stock` : "Increase quantity"}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-lg leading-none transition hover:bg-stone-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                        onClick={() =>
                          updateQuantity(product._id, 1, variantOptionId || "")
                        }
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    <p className="mt-1.5 text-sm tabular-nums text-stone-600">
                      ₹{unit} × {quantity}
                    </p>
                  </div>
                </li>
              );
              })}
            </ul>
          )}
        </div>

        {cart.length > 0 &&
          (isBottom ? (
            <div className="shrink-0 border-t border-stone-100 bg-stone-50/95 px-3 pt-2 pb-[max(0.65rem,env(safe-area-inset-bottom))]">
              <div className="flex items-end gap-3">
                <div className="min-w-0 flex-1 pb-0.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-500">
                    To pay
                  </p>
                  <p className="truncate text-xl font-extrabold tabular-nums leading-tight text-stone-900">
                    ₹{total.toFixed(0)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCart(false);
                    navigate(checkoutPath);
                  }}
                  className="shrink-0 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-600 px-4 py-3 text-center text-[13px] font-bold leading-snug text-white shadow-lg shadow-emerald-600/30 transition active:scale-[0.98] hover:from-emerald-700 hover:to-green-700 sm:px-5 sm:text-sm"
                >
                  Continue
                  <br />
                  <span className="font-extrabold">to checkout</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="shrink-0 border-t border-stone-200 bg-stone-50/60 px-4 py-3 sm:px-5 sm:py-4">
              <div className="mb-3 text-right text-base font-bold tabular-nums text-stone-900">
                Total: ₹{total.toFixed(0)}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCart(false);
                  navigate(checkoutPath);
                }}
                className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 py-3 text-sm font-bold text-white shadow-md shadow-orange-300/40 transition hover:from-orange-600 hover:to-amber-600"
              >
                Go to checkout
              </button>
            </div>
          ))}
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default CartDrawer;
